import { useCallback, useEffect, useRef, useState } from "react";
import { X, Volume2, VolumeX, ChevronLeft, ChevronRight, Instagram, Pause } from "lucide-react";

const Bars = ({ count, index, progress }) => (
  <div className="flex gap-1">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="h-[3px] flex-1 overflow-hidden bg-white/30">
        <div className="h-full bg-white" style={{ width: `${i < index ? 100 : i === index ? progress * 100 : 0}%` }} />
      </div>
    ))}
  </div>
);

export const StoryViewer = ({ items, start, onClose, onSeen }) => {
  const [index, setIndex] = useState(start);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const video = useRef(null);
  const item = items[index];

  const next = useCallback(() => (index < items.length - 1 ? setIndex(index + 1) : onClose()), [index, items.length, onClose]);
  const prev = useCallback(() => setIndex(Math.max(0, index - 1)), [index]);

  useEffect(() => {
    onSeen(item.id);
    setProgress(0);
    const v = video.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => { setMuted(true); v.muted = true; v.play().catch(() => {}); });
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [next, prev, onClose]);

  const hold = (on) => {
    const v = video.current;
    if (!v) return;
    setPaused(on);
    on ? v.pause() : v.play().catch(() => {});
  };

  return (
    <div data-testid="story-viewer" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl">
      <button onClick={prev} disabled={index === 0} data-testid="story-prev-btn" aria-label="Önceki" className="absolute left-6 hidden h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors hover:bg-white hover:text-black disabled:opacity-20 md:flex"><ChevronLeft className="h-6 w-6" /></button>
      <button onClick={next} data-testid="story-next-btn" aria-label="Sonraki" className="absolute right-6 hidden h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors hover:bg-white hover:text-black md:flex"><ChevronRight className="h-6 w-6" /></button>

      <div className="relative h-[100svh] w-full overflow-hidden bg-black md:h-[92svh] md:w-auto md:aspect-[9/16]">
        <video
          key={item.id}
          ref={video}
          src={item.video}
          poster={item.poster}
          muted={muted}
          playsInline
          autoPlay
          preload="auto"
          onTimeUpdate={(e) => e.currentTarget.duration && setProgress(e.currentTarget.currentTime / e.currentTarget.duration)}
          onEnded={next}
          data-testid="story-video"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 to-transparent" />

        <div className="absolute inset-0 flex" onPointerDown={() => hold(true)} onPointerUp={() => hold(false)} onPointerLeave={() => paused && hold(false)}>
          <button className="h-full w-1/3" aria-label="Önceki hikaye" data-testid="story-tap-prev" onClick={prev} />
          <div className="h-full w-1/3" />
          <button className="h-full w-1/3" aria-label="Sonraki hikaye" data-testid="story-tap-next" onClick={next} />
        </div>

        <div className="absolute inset-x-0 top-0 space-y-3 p-3">
          <Bars count={items.length} index={index} progress={progress} />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-black ring-2 ring-brand"><img src="/favicon.png" alt="FX" className="h-full w-full object-cover" /></span>
              <span className="text-sm font-semibold text-white">fxmotorsporlari</span>
              {paused && <Pause className="h-4 w-4 text-white" />}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setMuted(!muted)} data-testid="story-mute-btn" aria-label={muted ? "Sesi aç" : "Sesi kapat"} className="p-2 text-white">{muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}</button>
              <button onClick={onClose} data-testid="story-close-btn" aria-label="Kapat" className="p-2 text-white"><X className="h-6 w-6" /></button>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5">
          <p data-testid="story-title" className="max-w-[75%] text-sm font-semibold leading-snug text-white md:text-base">{item.title}</p>
          <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid="story-instagram-link" className="flex shrink-0 items-center gap-2 border border-white/30 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black">
            <Instagram className="h-4 w-4" /> Instagram
          </a>
        </div>
      </div>
    </div>
  );
};
